/**
 * 🛢️ Tank Level Gauge Component
 *
 * Reusable visual tank/cylinder representation with animated liquid fill.
 * Supports Water (Blue) and Diesel (Yellow) tanks with smooth animations.
 *
 * Features:
 * - Animated fill level (0-100%)
 * - Color-coded by tank type
 * - Threshold indicators (min/max lines)
 * - Accessibility-friendly (high contrast borders)
 * - Memoized for performance
 * - Loading state skeleton
 * - Null/invalid data handling
 *
 * Usage:
 * <TankLevelGauge
 *   tankType="water"
 *   level={72}
 *   capacity={1000}
 *   unit="L"
 *   minThreshold={20}
 *   maxThreshold={90}
 *   isLoading={false}
 * />
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { View, Text, StyleSheet, Dimensions, ViewStyle, Animated, Easing } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

// ═════════════════════════════════════════════════════════════════════
// TYPES
// ═════════════════════════════════════════════════════════════════════

export type TankType = 'water' | 'diesel' | 'gas';

export interface TankLevelGaugeProps {
  /** Tank type determines color (water=blue, diesel=yellow) */
  tankType: TankType;

  /** Current level percentage (0-100) */
  level: number | null | undefined;

  /** Tank capacity in units (optional, for display) */
  capacity?: number;

  /** Unit of measurement (L, gal, m³, etc.) */
  unit?: string;

  /** Minimum threshold percentage (shows warning line) */
  minThreshold?: number;

  /** Maximum threshold percentage (shows danger line) */
  maxThreshold?: number;

  /** Loading state */
  isLoading?: boolean;

  /** Custom height (default: 200) */
  height?: number;

  /** Custom width (default: 120) */
  width?: number;

  /** Theme colors */
  theme?: any;

  /** RTL support */
  isRTL?: boolean;
}

// ═════════════════════════════════════════════════════════════════════
// UTILITIES
// ═════════════════════════════════════════════════════════════════════

/**
 * Get color scheme based on tank type
 */
const getTankColors = (tankType: TankType) => {
  switch (tankType) {
    case 'water':
      return {
        fill: ['#60a5fa', '#3b82f6'] as [string, string], // Lighter blue gradient
        fillSolid: '#60a5fa',
        icon: 'water' as const,
        label: 'Water',
        labelAr: 'ماء',
      };
    case 'diesel':
      return {
        fill: ['#fcd34d', '#fbbf24'] as [string, string], // Lighter yellow/Amber gradient
        fillSolid: '#fcd34d',
        icon: 'flame' as const,
        label: 'Diesel',
        labelAr: 'ديزل',
      };
    case 'gas':
      return {
        fill: ['#a78bfa', '#8b5cf6'] as [string, string], // Lighter purple gradient
        fillSolid: '#a78bfa',
        icon: 'flame' as const,
        label: 'Gas',
        labelAr: 'غاز',
      };
    default:
      return {
        fill: ['#9ca3af', '#6b7280'] as [string, string], // Lighter gray fallback
        fillSolid: '#9ca3af',
        icon: 'help-circle' as const,
        label: 'Unknown',
        labelAr: 'غير معروف',
      };
  }
};

/**
 * Clamp level to 0-100 range
 */
const clampLevel = (level: number | null | undefined): number => {
  if (level === null || level === undefined || isNaN(level)) {
    return 0;
  }
  return Math.max(0, Math.min(100, level));
};

/**
 * Get status color based on level and thresholds
 */
const getStatusColor = (level: number, minThreshold?: number, maxThreshold?: number): string => {
  if (minThreshold !== undefined && level < minThreshold) {
    return '#ef4444'; // Red - Low level
  }
  if (maxThreshold !== undefined && level > maxThreshold) {
    return '#f97316'; // Orange - High level
  }
  return '#10b981'; // Green - Normal
};

/**
 * Format volume display
 */
const formatVolume = (value: number | null, unit: string = 'L'): string => {
  if (value === null) return '';
  if (value >= 1000 && unit !== 'm³') {
    return `${(value / 1000).toFixed(1)}k${unit}`;
  }
  return `${Math.round(value)}${unit}`;
};

// ═════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════

const TankLevelGauge: React.FC<TankLevelGaugeProps> = React.memo(
  ({
    tankType,
    level: rawLevel,
    capacity,
    unit = 'L',
    minThreshold,
    maxThreshold,
    isLoading = false,
    height = 200,
    width = 120,
    theme,
    isRTL = false,
  }) => {
    // Clamp and validate level
    const level = useMemo(() => clampLevel(rawLevel), [rawLevel]);
    const isValidLevel = rawLevel !== null && rawLevel !== undefined && !isNaN(rawLevel);

    // Get tank color scheme
    const colors = useMemo(() => getTankColors(tankType), [tankType]);

    // Status color
    const statusColor = useMemo(
      () => getStatusColor(level, minThreshold, maxThreshold),
      [level, minThreshold, maxThreshold],
    );

    // Volume display
    const volume = useMemo(() => (capacity ? (level / 100) * capacity : null), [level, capacity]);
    const remaining = useMemo(
      () => (capacity && volume !== null ? Math.max(0, capacity - volume) : null),
      [capacity, volume],
    );
    const volumeText = useMemo(() => formatVolume(volume, unit), [volume, unit]);
    const remainingText = useMemo(() => formatVolume(remaining, unit), [remaining, unit]);

    // Animation values using React Native Animated
    const fillHeight = useRef(new Animated.Value(0)).current;
    const waveOffset = useRef(new Animated.Value(0)).current;

    // Animate fill level when level changes.
    // IMPORTANT (iOS / New Architecture): these animations use `useNativeDriver: false`,
    // which drives view updates from JS via setNativeProps every frame. If the animation
    // keeps running while this component unmounts (e.g. navigating away after saving a
    // device), a frame can call setNativeProps on an already-freed Fabric shadow node,
    // causing a native use-after-free crash (SIGABRT). We therefore stop each animation
    // in the effect cleanup so nothing is left running past unmount.
    useEffect(() => {
      const animation = Animated.spring(fillHeight, {
        toValue: level,
        damping: 15,
        stiffness: 100,
        useNativeDriver: false,
      });
      animation.start();
      return () => {
        animation.stop();
      };
    }, [level, fillHeight]);

    // Continuous wave animation (subtle)
    useEffect(() => {
      const loop = Animated.loop(
        Animated.timing(waveOffset, {
          toValue: 1,
          duration: 2000,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: false,
        }),
      );
      loop.start();
      return () => {
        loop.stop();
      };
    }, [waveOffset]);

    // Hard-stop any in-flight animation when the gauge unmounts, as a final safety net
    // against setNativeProps firing on a freed native view.
    useEffect(
      () => () => {
        fillHeight.stopAnimation();
        waveOffset.stopAnimation();
      },
      [fillHeight, waveOffset],
    );

    // Animated fill style
    const animatedFillStyle = {
      height: fillHeight.interpolate({
        inputRange: [0, 100],
        outputRange: ['0%', '100%'],
      }),
    };

    // Default theme colors
    const themeColors = theme?.colors || {
      card: '#ffffff',
      text: '#1f2937',
      textSecondary: '#6b7280',
      border: '#e5e7eb',
      background: '#f9fafb',
    };

    const capHeight = Math.max(6, Math.round(width * 0.05));
    const capRadius = Math.round(width * 0.12);

    // ─────────────────────────────────────────────────────────────────
    // LOADING STATE
    // ─────────────────────────────────────────────────────────────────
    if (isLoading) {
      return (
        <View style={[styles.container, { backgroundColor: themeColors.card }]}>
          <View style={[styles.tankContainer, { height, width }]}>
            <View style={[styles.loadingSkeleton, { backgroundColor: themeColors.border }]} />
          </View>
          <View style={styles.infoContainer}>
            <View style={[styles.loadingText, { backgroundColor: themeColors.border }]} />
            <View
              style={[styles.loadingText, { backgroundColor: themeColors.border, width: 60 }]}
            />
          </View>
        </View>
      );
    }

    // ─────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────
    return (
      <View style={[styles.container, { backgroundColor: themeColors.card }]}>
        {/* Tank Icon & Label */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Ionicons name={colors.icon} size={20} color={colors.fillSolid} />
            <Text style={[styles.tankLabel, { color: themeColors.text }]}>
              {isRTL ? colors.labelAr : colors.label}
            </Text>
          </View>

          {/* Status Indicator */}
          <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
        </View>

        {/* Tank Visual */}
        <View style={styles.tankWrapper}>
          {/* Tank Container */}
          <View
            style={[
              styles.tankContainer,
              {
                height,
                width,
                borderColor: themeColors.border,
                backgroundColor: themeColors.background,
                borderRadius: Math.round(width * 0.12),
              },
            ]}
          >
            <View
              style={[
                styles.tankCap,
                styles.tankCapTop,
                {
                  height: capHeight,
                  borderRadius: Math.round(width * 0.12),
                  borderColor: themeColors.border,
                  backgroundColor: themeColors.card,
                },
              ]}
            />
            <View
              style={[
                styles.tankKnob,
                {
                  backgroundColor: colors.fillSolid,
                  borderColor: themeColors.border,
                },
              ]}
            />
            <View
              style={[
                styles.tankNeck,
                {
                  backgroundColor: themeColors.border,
                },
              ]}
            />
            {/* Threshold Lines */}
            {minThreshold !== undefined && (
              <View
                style={[
                  styles.thresholdLine,
                  {
                    bottom: `${minThreshold}%`,
                    backgroundColor: '#ef4444',
                  },
                ]}
              />
            )}

            {maxThreshold !== undefined && (
              <View
                style={[
                  styles.thresholdLine,
                  {
                    bottom: `${maxThreshold}%`,
                    backgroundColor: '#f97316',
                  },
                ]}
              />
            )}

            {/* Animated Liquid Fill */}
            {isValidLevel && level > 0 && (
              <Animated.View
                style={[
                  styles.liquidFill,
                  animatedFillStyle,
                  { opacity: 0.85 }, // Lighter visual weight
                ]}
              >
                <LinearGradient
                  colors={colors.fill}
                  style={styles.gradient}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 0, y: 1 }}
                />

                {/* Wave Effect (subtle) */}
                <View style={styles.waveOverlay} />
              </Animated.View>
            )}

            {/* Level Percentage (Inside Tank) */}
            <View style={styles.levelOverlay}>
              <Text
                style={[
                  styles.levelPercentage,
                  {
                    color: level > 50 ? '#ffffff' : themeColors.text,
                    textShadowColor: level > 50 ? 'rgba(0,0,0,0.3)' : 'transparent',
                    textShadowOffset: { width: 0, height: 1 },
                    textShadowRadius: level > 50 ? 2 : 0,
                  },
                ]}
              >
                {isValidLevel ? `${Math.round(level)}%` : '—'}
              </Text>
            </View>
          </View>

          {/* Scale Markers (0, 25, 50, 75, 100) */}
          <View style={styles.scaleContainer}>
            {[100, 75, 50, 25, 0].map((mark) => (
              <View key={mark} style={styles.scaleMark}>
                <View style={[styles.scaleTickLong, { backgroundColor: themeColors.border }]} />
                <Text style={[styles.scaleLabel, { color: themeColors.textSecondary }]}>
                  {mark}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* Info Section */}
        <View style={styles.infoContainer}>
          {/* Volume Display */}
          {volumeText && (
            <Text style={[styles.volumeText, { color: themeColors.text }]}>
              {isRTL ? `المستوى: ${volumeText}` : `Level: ${volumeText}`}
            </Text>
          )}

          {remainingText && (
            <Text style={[styles.capacityText, { color: themeColors.textSecondary }]}>
              {isRTL ? `المتبقي: ${remainingText}` : `Remaining: ${remainingText}`}
            </Text>
          )}

          {/* Capacity */}
          {capacity && (
            <Text style={[styles.capacityText, { color: themeColors.textSecondary }]}>
              {isRTL ? 'السعة' : 'Capacity'}: {capacity}
              {unit}
            </Text>
          )}

          {/* Status Message */}
          {!isValidLevel && (
            <Text style={[styles.statusText, { color: '#ef4444' }]}>
              {isRTL ? 'لا توجد بيانات' : 'No Data'}
            </Text>
          )}

          {isValidLevel && minThreshold !== undefined && level < minThreshold && (
            <Text style={[styles.statusText, { color: '#ef4444' }]}>
              ⚠️ {isRTL ? 'مستوى منخفض' : 'Low Level'}
            </Text>
          )}

          {isValidLevel && maxThreshold !== undefined && level > maxThreshold && (
            <Text style={[styles.statusText, { color: '#f97316' }]}>
              ⚠️ {isRTL ? 'مستوى مرتفع' : 'High Level'}
            </Text>
          )}
        </View>
      </View>
    );
  },
);

TankLevelGauge.displayName = 'TankLevelGauge';

export default TankLevelGauge;

// ═════════════════════════════════════════════════════════════════════
// STYLES
// ═════════════════════════════════════════════════════════════════════

const styles = StyleSheet.create({
  container: {
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    alignItems: 'stretch',
    width: '100%',
    maxHeight: 450,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 16,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  tankLabel: {
    fontSize: 16,
    fontWeight: '600',
  },
  statusDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  tankWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  tankContainer: {
    borderWidth: 5,
    borderRadius: 16,
    overflow: 'hidden',
    position: 'relative',
    justifyContent: 'flex-end',
  },
  tankCap: {
    position: 'absolute',
    left: -5,
    right: -5,
    borderWidth: 5,
    opacity: 0.95,
  },
  tankCapTop: {
    top: -8,
  },
  tankKnob: {
    position: 'absolute',
    top: -22,
    left: '50%',
    width: 24,
    height: 12,
    marginLeft: -12,
    borderRadius: 6,
    borderWidth: 2,
    zIndex: 6,
  },
  tankNeck: {
    position: 'absolute',
    top: -13,
    left: '50%',
    width: 12,
    height: 10,
    marginLeft: -6,
    borderRadius: 3,
    zIndex: 5,
  },
  liquidFill: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    borderRadius: 8,
  },
  gradient: {
    flex: 1,
  },
  waveOverlay: {
    position: 'absolute',
    top: -2,
    left: 0,
    right: 0,
    height: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: 2,
  },
  levelOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  levelPercentage: {
    fontSize: 28,
    fontWeight: '700',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
    textAlign: 'center',
  },
  thresholdLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 2,
    zIndex: 5,
    opacity: 0.7,
  },
  scaleContainer: {
    height: '100%',
    justifyContent: 'space-between',
    paddingVertical: 2,
  },
  scaleMark: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  scaleTickLong: {
    width: 10,
    height: 1,
  },
  scaleLabel: {
    fontSize: 10,
    fontWeight: '500',
    width: 24,
  },
  infoContainer: {
    alignItems: 'flex-start',
    gap: 4,
  },
  volumeText: {
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'left',
  },
  capacityText: {
    fontSize: 12,
    textAlign: 'left',
  },
  statusText: {
    fontSize: 13,
    fontWeight: '600',
    marginTop: 4,
    textAlign: 'left',
  },
  loadingSkeleton: {
    flex: 1,
    borderRadius: 8,
  },
  loadingText: {
    height: 16,
    width: 80,
    borderRadius: 4,
    marginVertical: 4,
  },
});

// ═════════════════════════════════════════════════════════════════════
// HELPER: Convert raw sensor value to percentage
// ═════════════════════════════════════════════════════════════════════

/**
 * Utility to convert raw sensor reading to percentage
 *
 * @param rawValue - Raw sensor value (cm, inches, etc.)
 * @param minValue - Sensor minimum (empty tank)
 * @param maxValue - Sensor maximum (full tank)
 * @returns Percentage (0-100)
 */
export const convertToPercentage = (
  rawValue: number | null | undefined,
  minValue: number,
  maxValue: number,
): number | null => {
  if (rawValue === null || rawValue === undefined || isNaN(rawValue)) {
    return null;
  }

  const range = maxValue - minValue;
  if (range === 0) return 0;

  const percentage = ((rawValue - minValue) / range) * 100;
  return Math.max(0, Math.min(100, percentage));
};
